import React, { useState } from "react";
import { api } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { DateTimeCell } from "./LedgerCells";
import { serviceTierName } from "../lib/serviceTiers";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Pager from "../components/ui/Pager";
import { usePagedList } from "./usePagedList";
import { PageLoadError, isForbidden } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";
import { Loader2, RefreshCcw, Headphones } from "lucide-react";

// Kept in step with academy_upsell_routes.SERVICE_LEAD_STATUSES / SERVICE_TIERS, which the
// list endpoint echoes back as `statuses` / `tiers` — the filters below are built from
// the response, so only the pill colours live here.
const STATUS_COLORS = {
  new: "bg-amber-100 text-amber-600",
  contacted: "bg-emerald-100 text-emerald-600",
  closed: "bg-slate-100 text-slate-800/70",
};

const PAGE_SIZE = 25;

export default function HqServiceLeads() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  // Changing a request's status is a CRM write (the PUT requires data.crm.write).
  const { manage: canWrite } = useScreenWrites("serviceLeads");
  const [filter, setFilter] = useState({ status: "", tier: "" });
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(null);
  // `reload` re-reads the SAME page and filters even when asked for after an awaited request
  // (a status update); `retry` repeats a failed read.
  const { data, totalForCurrent, loading, loadFailed, loadError, reload, retry } = usePagedList({
    path: "/super-admin/hq/service-leads", filters: filter, page, setPage, pageSize: PAGE_SIZE,
  }); // data: { total, items, by_status, statuses, tiers }

  const applyFilter = (patch) => {
    setFilter((cur) => ({ ...cur, ...patch }));
    setPage(1);
  };

  const setStatus = async (lead, status) => {
    setBusy(lead.id);
    try {
      await api.put(`/super-admin/hq/service-leads/${lead.id}`, { status });
      toast.success(t("sl.updated"));
      reload();
    } catch (e) {
      // The server's own text is English and never shown; a refusal for lack of permission says so.
      toast.error(t(isForbidden(e) ? "sl.noPermission" : "sl.error"));
    } finally {
      setBusy(null);
    }
  };

  const statuses = data?.statuses || [];
  const tiers = data?.tiers || [];

  return (
    <div data-testid="hq-service-leads">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800 flex items-center gap-3">
            <Headphones className="w-8 h-8 text-amber-600" /> {t("sl.title")}
          </h1>
          <p className="text-slate-500">{t("sl.subtitle")}</p>
        </div>
        <Button onClick={reload} variant="outline" className="border-slate-200 text-slate-800/70" data-testid="reload-leads">
          <RefreshCcw className="w-4 h-4 mr-2" /> {t("sl.refresh")}
        </Button>
      </div>

      {data?.by_status && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => applyFilter({ status: filter.status === s ? "" : s })}
              className={`text-left p-4 rounded-xl border transition ${filter.status === s ? "border-sol-gold/40 bg-sol-gold/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              data-testid={`lead-status-${s}`}
            >
              <div className={`inline-flex text-xs rounded-full px-2 py-0.5 ${STATUS_COLORS[s] || "bg-slate-100"}`}>{t(`sl.status.${s}`)}</div>
              <div className="font-sans font-semibold text-3xl mt-2">{data.by_status[s] || 0}</div>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <select
          value={filter.tier}
          onChange={(e) => applyFilter({ tier: e.target.value })}
          className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
          data-testid="filter-tier"
        >
          <option value="">{t("sl.allTiers")}</option>
          {tiers.map((x) => <option key={x} value={x}>{serviceTierName(x)}</option>)}
        </select>
        <p className="text-xs text-slate-400">{t("sl.automationHint")}</p>
      </div>

      <PageLoadError error={loadFailed && !loading ? loadError : null} onRetry={retry} testId="hq-service-leads-load-error" />
      {loading && (loadFailed || !data) && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}

      {data && !loadFailed && (
        <div
          className={`rounded-2xl bg-white border border-slate-200 overflow-hidden transition-opacity ${loading ? "opacity-60" : ""}`}
          aria-busy={loading}
          data-testid="hq-service-leads-table"
        >
          {/* The rows of the previous filter stay (dimmed) while new filters load; their total does not. */}
          {totalForCurrent !== null && (
            <div className="px-4 py-3 text-xs text-slate-400 border-b border-slate-100" data-testid="hq-service-leads-total">{t("sl.total", { n: totalForCurrent })}</div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400 tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">{t("sl.colTenant")}</th>
                <th className="text-left px-4 py-3">{t("sl.colTier")}</th>
                <th className="text-left px-4 py-3">{t("sl.colEmail")}</th>
                <th className="text-left px-4 py-3">{t("sl.colMessage")}</th>
                <th className="text-left px-4 py-3">{t("sl.colCreated")}</th>
                <th className="text-left px-4 py-3">{t("sl.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((lead) => (
                <tr key={lead.id} className="border-t border-slate-100 align-top" data-testid={`lead-${lead.id}`}>
                  <td className="px-4 py-3 font-medium">
                    {lead.tenant_name || <code className="text-xs text-slate-500">{lead.tenant_id}</code>}
                    {lead.worked_by && (
                      <div className="text-[10px] text-slate-400 mt-0.5">{t("sl.workedBy")} {lead.worked_by}</div>
                    )}
                  </td>
                  <td className="px-4 py-3"><span className="text-xs text-amber-600">{serviceTierName(lead.tier)}</span></td>
                  <td className="px-4 py-3 text-slate-500">
                    <a href={`mailto:${lead.owner_email}`} className="hover:underline">{lead.owner_email || "—"}</a>
                  </td>
                  <td className="px-4 py-3 text-slate-800/70 max-w-xs whitespace-pre-wrap">
                    {lead.message || <span className="text-slate-400">{t("sl.noMessage")}</span>}
                  </td>
                  {/* Date, then HH:MM: the ledgers' cell, not the long locale string with seconds. */}
                  <td className="px-4 py-3 text-slate-500" data-testid={`lead-created-${lead.id}`}>
                    <DateTimeCell value={lead.created_at} lang={lang} />
                  </td>
                  <td className="px-4 py-3">
                    {canWrite ? (
                      <select
                        value={lead.status}
                        disabled={busy === lead.id}
                        onChange={(e) => setStatus(lead, e.target.value)}
                        className={`rounded-full text-xs px-2 py-1 border border-slate-200 ${STATUS_COLORS[lead.status] || "bg-slate-100"}`}
                        data-testid={`lead-status-select-${lead.id}`}
                      >
                        {statuses.map((s) => <option key={s} value={s} className="bg-white text-slate-800">{t(`sl.status.${s}`)}</option>)}
                      </select>
                    ) : (
                      <span className={`inline-flex rounded-full text-xs px-2 py-1 ${STATUS_COLORS[lead.status] || "bg-slate-100"}`} data-testid={`lead-status-${lead.id}`}>
                        {t(`sl.status.${lead.status}`, undefined, lead.status)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400" data-testid="hq-service-leads-empty">{t("sl.empty")}</td></tr>
              )}
            </tbody>
          </table>
          {data.total > PAGE_SIZE && (
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={data.total}
              shown={data.items.length}
              onPage={setPage}
              infoText={(v) => t("sal.pagerInfo", v)}
              prevLabel={t("sal.pagerPrev")}
              nextLabel={t("sal.pagerNext")}
              testId="hq-service-leads-pager"
              tone="slate"
            />
          )}
        </div>
      )}
    </div>
  );
}
